import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Upload, FileText, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { StrataLogo } from "@/components/StrataLogo";

interface CSVImportWizardProps {
  tableId: string;
  existingFields: any[];
  onImportComplete: () => void;
}

export function CSVImportWizard({ tableId, existingFields, onImportComplete }: CSVImportWizardProps) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [csvFile, setCSVFile] = useState<File | null>(null);
  const [csvData, setCSVData] = useState<any[]>([]);
  const [csvHeaders, setCSVHeaders] = useState<string[]>([]);
  const [step, setStep] = useState<"upload" | "mapping" | "importing">("upload");
  const [columnMapping, setColumnMapping] = useState<any[]>([]);
  const [isMapping, setIsMapping] = useState(false);
  const [importProgress, setImportProgress] = useState(0);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === "text/csv") {
      setCSVFile(file);
      parseCSV(file);
    } else {
      toast.error("Please select a valid CSV file");
    }
  };

  const handleDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file && file.type === "text/csv") {
      setCSVFile(file);
      parseCSV(file);
    } else {
      toast.error("Please drop a valid CSV file");
    }
  }, []);

  const parseCSV = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split("\n").filter(line => line.trim());
      const headers = lines[0].split(",").map(h => h.trim());
      const data = lines.slice(1).map(line => {
        const values = line.split(",").map(v => v.trim());
        const row: any = {};
        headers.forEach((header, index) => {
          row[header] = values[index];
        });
        return row;
      });

      setCSVHeaders(headers);
      setCSVData(data);
      setStep("mapping");
      performAIMapping(headers, data);
    };
    reader.readAsText(file);
  };

  const performAIMapping = async (headers: string[], sampleData: any[]) => {
    setIsMapping(true);

    try {
      const { data, error } = await supabase.functions.invoke("map-csv-columns", {
        body: {
          csvHeaders: headers,
          csvSample: sampleData.slice(0, 5),
          targetFields: existingFields.map(f => ({ name: f.name, type: f.type }))
        }
      });

      if (error) throw error;

      if (data.error) {
        toast.error(data.error);
        return;
      }

      setColumnMapping(data.mappings || []);
      toast.success(`Mapped ${data.mappings?.length || 0} columns with ${Math.round((data.overallConfidence || 0) * 100)}% confidence`);
    } catch (error: any) {
      console.error("Error mapping columns:", error);
      toast.error("Failed to map columns automatically");
      
      // Fallback to basic mapping
      setColumnMapping(headers.map(header => ({
        csvColumn: header,
        targetField: existingFields.find(f => 
          f.name.toLowerCase() === header.toLowerCase()
        )?.name || null,
        confidence: 0.5
      })));
    } finally {
      setIsMapping(false);
    }
  };

  const handleImport = async () => {
    setStep("importing");
    const totalRecords = csvData.length;
    let imported = 0;

    try {
      for (const row of csvData) {
        const recordData: any = {};
        
        columnMapping.forEach(mapping => {
          if (mapping.targetField && row[mapping.csvColumn]) {
            recordData[mapping.targetField] = row[mapping.csvColumn];
          }
        });

        await supabase.from("records").insert([{
          table_id: tableId,
          data: recordData,
          created_by: user!.id
        }]);

        imported++;
        setImportProgress((imported / totalRecords) * 100);
      }

      toast.success(`Successfully imported ${imported} records!`);
      setOpen(false);
      resetWizard();
      onImportComplete();
    } catch (error: any) {
      console.error("Error importing data:", error);
      toast.error(`Import failed at record ${imported + 1}`);
    }
  };

  const resetWizard = () => {
    setCSVFile(null);
    setCSVData([]);
    setCSVHeaders([]);
    setStep("upload");
    setColumnMapping([]);
    setImportProgress(0);
  };

  return (
    <>
      <Button onClick={() => setOpen(true)} variant="outline">
        <Upload className="h-4 w-4 mr-2" />
        Import CSV
      </Button>

      <Dialog open={open} onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (!isOpen) resetWizard();
      }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Import CSV Data</DialogTitle>
            <DialogDescription>
              Upload a CSV file and we'll intelligently map columns to your table fields
            </DialogDescription>
          </DialogHeader>

          {step === "upload" && (
            <div className="space-y-4">
              <div
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                className="border-2 border-dashed rounded-lg p-12 text-center cursor-pointer hover:border-primary transition-colors"
              >
                <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground mb-2">
                  Drag and drop your CSV file here, or click to browse
                </p>
                <Input
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="csv-upload"
                />
                <Label htmlFor="csv-upload">
                  <Button variant="outline" asChild>
                    <span>Select CSV File</span>
                  </Button>
                </Label>
              </div>
            </div>
          )}

          {step === "mapping" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <StrataLogo size="md" />
                  <h3 className="font-semibold">AI Column Mapping</h3>
                </div>
                {isMapping && <Loader2 className="h-4 w-4 animate-spin" />}
              </div>

              <div className="border rounded-lg divide-y max-h-96 overflow-y-auto">
                {columnMapping.map((mapping, index) => (
                  <div key={index} className="p-3 flex items-center justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-sm">{mapping.csvColumn}</div>
                      {mapping.transformNeeded && (
                        <div className="text-xs text-muted-foreground mt-1">
                          Transform: {mapping.transformNeeded}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {mapping.targetField ? (
                        <>
                          <Badge variant="outline" className="bg-green-500/10">
                            → {mapping.targetField}
                          </Badge>
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                        </>
                      ) : (
                        <>
                          <Badge variant="outline" className="bg-yellow-500/10">
                            New field
                          </Badge>
                          <AlertCircle className="h-4 w-4 text-yellow-600" />
                        </>
                      )}
                    </div>

                    <div className="ml-4 text-xs text-muted-foreground">
                      {Math.round(mapping.confidence * 100)}%
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setStep("upload")} className="flex-1">
                  Back
                </Button>
                <Button onClick={handleImport} className="flex-1">
                  Import {csvData.length} Records
                </Button>
              </div>
            </div>
          )}

          {step === "importing" && (
            <div className="space-y-4 py-8">
              <div className="text-center">
                <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-primary" />
                <h3 className="font-semibold mb-2">Importing records...</h3>
                <p className="text-sm text-muted-foreground">
                  Please wait while we import your data
                </p>
              </div>
              
              <Progress value={importProgress} className="w-full" />
              
              <p className="text-center text-sm text-muted-foreground">
                {Math.round(importProgress)}% complete
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
